import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  Shield, AlertTriangle, Scale, TrendingDown, Brain, CheckCircle, 
  Rocket, Lock, Target, Clock, ArrowRight, Building2, Users,
  MessageSquare, Eye, ShieldCheck, Lightbulb, HeartHandshake,
  RefreshCw, BarChart3, Headphones, Briefcase, Factory
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function SolucaoBrasilOuvidoriaPage() {
  // Schema.org FAQ for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "O que é uma ouvidoria corporativa?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Uma ouvidoria corporativa é um canal formal de escuta que permite que colaboradores, clientes e parceiros registrem reclamações, sugestões e críticas de forma organizada. É um instrumento de governança, gestão de riscos e melhoria contínua."
        }
      },
      {
        "@type": "Question",
        "name": "Por que pequenas e médias empresas precisam de ouvidoria?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sem um canal formal de escuta, problemas operacionais, conflitos internos e insatisfações se acumulam em silêncio até virarem crises, processos ou perda de clientes. A ouvidoria permite detectar e corrigir problemas antes que se tornem grandes prejuízos."
        }
      },
      {
        "@type": "Question",
        "name": "Qual a diferença entre ouvidoria e canal de denúncias?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A ouvidoria é um canal amplo para reclamações, sugestões e críticas gerais. O canal de denúncias é focado em irregularidades e condutas antiéticas. Ambos são complementares e importantes para a governança corporativa."
        }
      }
    ]
  };

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "OmbudNow",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "Sistema de ouvidoria online para empresas brasileiras. Plataforma segura de ouvidoria corporativa para SMBs.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "BRL"
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e]">
      <Helmet>
        <title>Ouvidoria para Empresas | Ouvidoria Corporativa Online | OmbudNow</title>
        <meta name="description" content="Sistema de ouvidoria online para empresas brasileiras. Transforme reclamações, conflitos e falhas internas em gestão, prevenção e confiança. Plataforma simples e segura." />
        <meta name="keywords" content="ouvidoria corporativa, ouvidoria empresarial, ouvidoria online, sistema de ouvidoria, canal de ouvidoria, ouvidoria para empresas, gestão de reclamações, ouvidoria SMB" />
        <link rel="canonical" href="https://ombudnow.com/solucaobrasilouvidoria" />
        <meta property="og:title" content="Ouvidoria para Empresas | OmbudNow" />
        <meta property="og:description" content="Transforme reclamações, conflitos e falhas internas em gestão, prevenção e confiança com o OmbudNow." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ombudnow.com/solucaobrasilouvidoria" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#1a1a2e]/95 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#f5a623] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#1a1a2e]" />
              </div>
              <span className="text-xl font-bold text-white">OmbudNow</span>
            </Link>
            <Link to="/pt">
              <Button className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] font-semibold">
                Criar minha ouvidoria
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className="px-6 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#0EA5E9]/20 text-[#0EA5E9] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <MessageSquare className="w-4 h-4" />
              Ouvidoria para Empresas
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Transforme reclamações, conflitos e falhas internas em gestão, prevenção e confiança
            </h1>
            
            <p className="text-xl text-[#0EA5E9] font-medium mb-8">
              OmbudNow — sistema de ouvidoria corporativa simples, seguro e adequado à realidade das SMBs
            </p>
            
            <p className="text-lg text-slate-300 mb-4 leading-relaxed">
              A maioria das pequenas e médias empresas no Brasil não tem ouvidoria.
            </p>
            <p className="text-lg text-slate-400 mb-10 leading-relaxed">
              Não porque não precisa — mas porque <strong className="text-white">não sabe o quanto perde sem ela</strong>.
            </p>
            
            <p className="text-slate-300 mb-10 max-w-3xl mx-auto">
              Sem um canal formal de escuta, problemas operacionais, conflitos internos e insatisfações se acumulam em silêncio até virarem crises, processos ou perda de clientes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pt">
                <Button size="lg" className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white text-lg px-8 font-semibold h-14 w-full sm:w-auto">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Criar ouvidoria para minha empresa
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 text-lg px-8 h-14"
                onClick={() => document.getElementById('problema').scrollIntoView({ behavior: 'smooth' })}
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                Minha empresa está perdendo informações críticas?
              </Button>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section id="problema" className="px-6 py-16 bg-gradient-to-b from-red-950/30 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O problema invisível dentro das empresas
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">
              Quando uma empresa não possui uma ouvidoria corporativa estruturada, o que acontece é previsível:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: MessageSquare, text: "Reclamações não chegam à gestão" },
                { icon: Lightbulb, text: "Funcionários desistem de sugerir melhorias" },
                { icon: RefreshCw, text: "Conflitos operacionais se repetem" },
                { icon: Users, text: "Clientes insatisfeitos não são ouvidos" },
                { icon: TrendingDown, text: "Pequenos problemas viram grandes prejuízos" },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-red-500/20">
                  <item.icon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-red-950/50 rounded-2xl p-6 border border-red-500/30 mb-8">
              <p className="text-white text-lg font-medium mb-2">O maior risco não é ouvir reclamações.</p>
              <p className="text-slate-300">É <strong className="text-red-400">não ouvir nada</strong> — e achar que está tudo bem.</p>
            </div>
            
            <Link to="/pt/guias/diferenca-canal-denuncias-ouvidoria" className="inline-flex items-center gap-2 text-[#0EA5E9] hover:underline font-medium">
              📘 Entender o papel da ouvidoria empresarial
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Common Mistake Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O erro mais comum das SMBs
              </h2>
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10 mb-8">
              <div className="space-y-3 text-slate-400 italic">
                <p>"Quem tem problema fala direto com o gestor."</p>
                <p>"Nossa equipe é pequena."</p>
                <p>"Se algo estivesse errado, a gente saberia."</p>
              </div>
            </div>
            
            <p className="text-[#f5a623] font-semibold mb-6">A realidade:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Pessoas não reclamam diretamente",
                "O medo de exposição silencia feedbacks",
                "Problemas operacionais raramente sobem à liderança",
                "A empresa toma decisões sem informação real da base"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <span className="text-red-400">✗</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-purple-950/50 rounded-2xl p-6 border border-purple-500/30">
              <p className="text-white text-lg font-medium">
                Sem um sistema de ouvidoria, <strong className="text-purple-400">a gestão opera no escuro</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Governance Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Ouvidoria, governança e boas práticas empresariais
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-4">
              Uma ouvidoria empresarial não é apenas um canal de reclamações.
            </p>
            <p className="text-slate-400 mb-8">
              Ela é um <strong className="text-white">instrumento de governança, gestão de riscos e melhoria contínua</strong>.
            </p>
            
            <p className="text-slate-300 mb-6">Empresas maduras utilizam a ouvidoria para:</p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { icon: Eye, text: "Detectar falhas operacionais" },
                { icon: Users, text: "Identificar conflitos internos" },
                { icon: Scale, text: "Reduzir riscos trabalhistas" },
                { icon: HeartHandshake, text: "Melhorar clima organizacional" },
                { icon: ShieldCheck, text: "Demonstrar transparência e boa-fé" },
                { icon: Briefcase, text: "Fortalecer a relação com clientes e parceiros" },
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3 bg-[#0EA5E9]/10 rounded-xl p-4 border border-[#0EA5E9]/30">
                  <item.icon className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <Link to="/pt">
              <Button className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white font-semibold">
                <ShieldCheck className="w-5 h-5 mr-2" />
                Fortalecer a governança da minha empresa
              </Button>
            </Link>
          </div>
        </section>

        {/* Cost Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-orange-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O custo silencioso de não ter ouvidoria
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">Sem uma ouvidoria formal:</p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-3">
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> Problemas se repetem sem diagnóstico
                </p>
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> Clientes insatisfeitos simplesmente vão embora
                </p>
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> Funcionários desmotivados pedem demissão
                </p>
              </div>
              <div className="space-y-3">
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> A empresa perde eficiência operacional
                </p>
                <p className="text-slate-400 flex items-center gap-2">
                  <span className="text-red-400">✗</span> A reputação se deteriora lentamente
                </p>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
              <p className="text-slate-300">
                Tudo isso acontece <strong className="text-white">antes de virar um problema jurídico</strong> — e muitas vezes poderia ser evitado.
              </p>
            </div>
          </div>
        </section>

        {/* Product Section */}
        <section className="px-6 py-16 bg-gradient-to-b from-[#0EA5E9]/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                O que é o OmbudNow e como ele atua como ouvidoria
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-8">
              O OmbudNow é uma plataforma de <strong className="text-white">ouvidoria online</strong> desenvolvida para empresas que precisam:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                "Centralizar reclamações, sugestões e críticas",
                "Criar um canal seguro e organizado de escuta",
                "Gerar registros estruturados",
                "Apoiar decisões gerenciais com dados reais",
                "Demonstrar transparência e responsabilidade"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <Target className="w-5 h-5 text-[#0EA5E9]" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-300 mb-6">Com o OmbudNow, sua empresa passa a ter:</p>
            
            <div className="bg-white/5 rounded-2xl p-8 border border-[#0EA5E9]/30 mb-8">
              <div className="space-y-4">
                {[
                  "Ouvidoria corporativa online",
                  "Canal confidencial e estruturado",
                  "Registro e histórico de manifestações",
                  "Organização por tipo, área e prioridade",
                  "Base real para decisões estratégicas"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#22c55e]" />
                    <span className="text-white font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <Link to="/pt">
              <Button size="lg" className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white text-lg px-8 font-semibold">
                <MessageSquare className="w-5 h-5 mr-2" />
                Ativar sistema de ouvidoria agora
              </Button>
            </Link>
          </div>
        </section>

        {/* Intelligence Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#22c55e]/20 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-[#22c55e]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Ouvidoria não é problema. É inteligência empresarial.
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-green-500/10 rounded-2xl p-6 border border-green-500/30">
                <h3 className="text-white font-semibold mb-4">Empresas que escutam:</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Corrigem antes de escalar</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Retêm talentos</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Fidelizam clientes</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Reduzem conflitos</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Tomam decisões melhores</li>
                </ul>
              </div>
              
              <div className="bg-red-500/10 rounded-2xl p-6 border border-red-500/30">
                <h3 className="text-white font-semibold mb-4">Empresas que não escutam:</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Reagem tarde</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Gastam mais</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Perdem confiança</li>
                  <li className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" /> Vivem apagando incêndios</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#f5a623]/20 rounded-xl flex items-center justify-center">
                <Rocket className="w-6 h-6 text-[#f5a623]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Como implementar uma ouvidoria na sua empresa
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-8">Com o OmbudNow, o processo é simples e rápido:</p>
            
            <div className="space-y-4 mb-10">
              {[
                { step: "1️⃣", text: "Criar sua conta" },
                { step: "2️⃣", text: "Configurar o canal de ouvidoria" },
                { step: "3️⃣", text: "Comunicar colaboradores e clientes" },
                { step: "4️⃣", text: "Receber, classificar e tratar manifestações" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                  <span className="text-2xl">{item.step}</span>
                  <span className="text-white text-lg">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-4 text-slate-400 mb-8">
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem complexidade</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem consultorias caras</span>
              <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#22c55e]" /> Sem burocracia</span>
            </div>
            
            <Link to="/pt">
              <Button size="lg" className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] text-lg px-8 font-semibold">
                <Rocket className="w-5 h-5 mr-2" />
                Implementar ouvidoria hoje
              </Button>
            </Link>
          </div>
        </section>

        {/* Security Section */}
        <section className="px-6 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Confidencialidade, organização e confiança
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[
                { icon: Shield, text: "Ambiente seguro" },
                { icon: Lock, text: "Acesso controlado" },
                { icon: Eye, text: "Confidencialidade garantida" },
                { icon: CheckCircle, text: "Registro organizado" },
                { icon: ShieldCheck, text: "Transparência no tratamento" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
                  <item.icon className="w-5 h-5 text-[#0EA5E9]" />
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
            
            <p className="text-slate-400 italic">
              Porque ninguém fala se não confia — e gestão sem escuta falha.
            </p>
          </div>
        </section>

        {/* Target Audience Section */}
        <section className="px-6 py-16 bg-white/[0.02]">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Para quem a ouvidoria OmbudNow é indicada
              </h2>
            </div>
            
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: Building2, text: "Pequenas e médias empresas" },
                { icon: Rocket, text: "Startups em crescimento" },
                { icon: Headphones, text: "Empresas de serviços" },
                { icon: Factory, text: "Indústrias" },
                { icon: Briefcase, text: "Escritórios" },
                { icon: Users, text: "Negócios B2B e B2C" }
              ].map((item, index) => (
                <span key={index} className="inline-flex items-center gap-2 bg-purple-500/20 text-purple-300 px-4 py-2 rounded-full text-sm font-medium">
                  <item.icon className="w-4 h-4" />
                  {item.text}
                </span>
              ))}
            </div>
            
            <p className="text-slate-300 text-lg">
              Se sua empresa lida com pessoas, processos ou clientes, <strong className="text-white">ouvidoria não é opcional</strong>.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="px-6 py-20 bg-gradient-to-t from-[#0EA5E9]/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#0EA5E9]/20 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#0EA5E9]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                A pergunta que define maturidade empresarial
              </h2>
            </div>
            
            <p className="text-slate-300 text-lg mb-6">
              Se hoje alguém tiver um problema sério, sua empresa:
            </p>
            
            <div className="space-y-3 mb-8">
              {[
                "Tem um canal formal para escuta?",
                "Registra e acompanha manifestações?",
                "Aprende com erros recorrentes?",
                "Usa feedback para melhorar processos?"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 text-slate-300">
                  <span className="text-[#0EA5E9]">❓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
            
            <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10 mb-10">
              <p className="text-white text-lg">
                Se a resposta for <strong className="text-red-400">"não"</strong>, a empresa está perdendo valor diariamente.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Link to="/pt">
                <Button size="lg" className="bg-[#0EA5E9] hover:bg-[#0EA5E9]/90 text-white text-xl px-10 font-bold h-16 w-full sm:w-auto">
                  🗣️ Criar ouvidoria para minha empresa agora
                </Button>
              </Link>
              <Link to="/pt/guias/como-implementar-canal-denuncias-ouvidoria-smb">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 h-16 w-full sm:w-auto">
                  📊 Avaliar maturidade de gestão
                </Button>
              </Link>
            </div>
            
            <div className="text-center bg-[#0EA5E9]/10 rounded-2xl p-8 border border-[#0EA5E9]/30">
              <p className="text-xl text-white font-semibold mb-2">
                Ouvidoria não é reclamação.
              </p>
              <p className="text-[#0EA5E9] text-lg">
                É gestão, prevenção e inteligência organizacional.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Related Solutions Section */}
      <section className="px-6 py-12 bg-white/[0.02] border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg font-semibold text-white mb-6 text-center">Soluções Relacionadas</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/solucaoBrasil" className="group bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#f5a623]/50 transition-all">
              <div className="flex items-center gap-3 mb-2">
                <Lock className="w-5 h-5 text-[#f5a623]" />
                <span className="text-white font-medium group-hover:text-[#f5a623] transition-colors">Canal de Denúncias</span>
              </div>
              <p className="text-slate-400 text-sm">Proteja sua empresa contra riscos legais e trabalhistas</p>
            </Link>
            <Link to="/solucaobrasilcompliance" className="group bg-white/5 rounded-xl p-5 border border-white/10 hover:border-[#22c55e]/50 transition-all">
              <div className="flex items-center gap-3 mb-2">
                <ShieldCheck className="w-5 h-5 text-[#22c55e]" />
                <span className="text-white font-medium group-hover:text-[#22c55e] transition-colors">Compliance Empresarial</span>
              </div>
              <p className="text-slate-400 text-sm">Proteja sua empresa com governança corporativa</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#f5a623] rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#1a1a2e]" />
              </div>
              <span className="text-xl font-bold text-white">OmbudNow</span>
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <Link to="/pt/sobre" className="text-slate-400 hover:text-white transition-colors">Sobre</Link>
              <Link to="/pt/contato" className="text-slate-400 hover:text-white transition-colors">Contato</Link>
              <Link to="/pt/guias" className="text-slate-400 hover:text-white transition-colors">Guias</Link>
              <Link to="/pt/politica-de-privacidade" className="text-slate-400 hover:text-white transition-colors">Privacidade</Link>
              <Link to="/pt/termos-de-uso" className="text-slate-400 hover:text-white transition-colors">Termos</Link>
            </div>
            
            <p className="text-slate-500 text-sm">
              © 2025 OmbudNow. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
